import { useProfile } from "@/context/useProfileProps";
import { getSiteProps } from "@/utils";
import {
  INDIAN_PHONE_NUMBER_REGEX,
  NON_INDIAN_PHONE_NUMBER_REGEX,
} from "@/utils/regex";
import { MobileTextInput } from "@monsterindia/global-ui-components";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { useSaveContact, useSendMobileOTP } from "../../query";
import { ChangeMobileNumberProps } from "../../types";

const ChangeMobileNumber = ({
  mobileNumber,
  countryCode,
  updateOTPIdHandler,
  formRef,
  changeMobileNumberType,
  setLoading,
  executeRecaptcha,
  closeMobileModal,
}: ChangeMobileNumberProps) => {
  const { mutate: sendMobileOTP } = useSendMobileOTP();
  const { mutate: saveContact } = useSaveContact();
  const {
    translation: { errorMessages },
    showToast,
    handleEventTracking,
  } = useProfile();
  const siteConfig = getSiteProps(window.location.hostname);
  const isFromRexmonster = siteConfig?.site_map === "rexmonster";
  const {
    handleSubmit,
    formState: { errors },
    control,
    watch,
    getValues,
    setValue,
    clearErrors,
    setError,
  } = useForm({
    defaultValues: {
      countryCode: countryCode
        ? { text: `+${countryCode}` }
        : {
            text: siteConfig?.country_isd_code
              ? `+${siteConfig?.country_isd_code}`
              : "+91",
          },
      number: mobileNumber ? mobileNumber : "",
    },
  });

  useEffect(() => {
    if (countryCode) {
      setValue("countryCode", {
        text: `+${countryCode}`,
      });
    }

    if (mobileNumber) {
      setValue("number", mobileNumber);
    }
  }, [mobileNumber, countryCode]);

  const sendMobileOTPHandler = async (
    mobileNumber: string,
    countryCode: string,
  ) => {
    setLoading(true);
    const { reCaptchaToken, reCaptchaAction } =
      await executeRecaptcha("sendotp");

    const apiData = {
      username: `+${countryCode}${mobileNumber}`,
      reCaptchaToken,
      reCaptchaAction,
    };

    // api call to send mobile otp
    if (isFromRexmonster) {
      sendMobileOTP(apiData, {
        onSuccess: (data) => {
          changeMobileNumberType(mobileNumber, countryCode);
          updateOTPIdHandler(data?.id);
          setLoading(false);
          let urlParams = new URLSearchParams(window.location.search);
          const isApp = urlParams.get("app") === "true";
          handleEventTracking({
            eventType: "eventPush",
            eventName: "ProfilePage_Section_Added",
            eventProps: {
              Contact_Info_Added: `phone number = +${countryCode} ${mobileNumber}`,
              Source: isApp ? "app" : "web",
            },
          });
          showToast({
            show: true,
            type: "success",
            message: "OTP sent successfully",
          });
        },
        onError: (err) => {
          setLoading(false);
          showToast({
            show: true,
            type: "error",
            message:
              err instanceof Error
                ? err.message
                : errorMessages.somethingWentWrong,
          });
        },
      });
    } else {
      saveContact(
        { mobileNumber, countryCode },
        {
          onSuccess: (data) => {
            closeMobileModal();
            setLoading(false);
            showToast({
              show: true,
              type: "success",
              message: data?.successMessage || "Contact saved successfully",
            });
          },
          onError: (err) => {
            setLoading(false);
            showToast({
              show: true,
              type: "error",
              message:
                err instanceof Error
                  ? err.message
                  : errorMessages.somethingWentWrong,
            });
          },
        },
      );
    }
  };

  const onSubmit = async (data: any) => {
    if (!data?.number) {
      setError("number", { message: "Mobile Number is required" });
      return;
    }

    if (
      !data?.number.match(
        data.countryCode?.text && data.countryCode?.text === "+91"
          ? INDIAN_PHONE_NUMBER_REGEX
          : NON_INDIAN_PHONE_NUMBER_REGEX,
      )
    ) {
      setError("number", { message: "Invalid Mobile Number" });
      return;
    }

    const newCountryCode = data.countryCode?.text
      ? (data.countryCode?.text?.slice(1) ?? countryCode)
      : countryCode;

    sendMobileOTPHandler(data.number, newCountryCode);
  };

  const watchedCountryCode = watch("countryCode");
  const countryISD = watchedCountryCode?.text
    ? watchedCountryCode?.text
    : countryCode
      ? `+${countryCode}`
      : siteConfig?.country_isd_code
        ? `+${siteConfig?.country_isd_code}`
        : "+91";

  return (
    <form
      ref={formRef}
      className="min-h-[40vh] w-full md:max-h-[370px]"
      onSubmit={handleSubmit(onSubmit)}
      noValidate
    >
      <Controller
        name="number"
        control={control}
        rules={{
          required: "Mobile Number is required",
        }}
        render={({ field }) => {
          return (
            <MobileTextInput
              mobileNumber={field.value ?? mobileNumber ?? ""}
              setMobileNumber={(value: string) => {
                clearErrors("number");
                setValue("number", value);
              }}
              required={true}
              countryCode={{
                text: countryISD,
              }}
              setCountryCode={(value: unknown) => {
                setValue("countryCode", {
                  text: (value as any)?.text,
                });
                setValue("number", "");
              }}
              errorText={errors?.number ? errors?.number?.message : ""}
            />
          );
        }}
      />
    </form>
  );
};

export default ChangeMobileNumber;
